import { PldDescriptionSources, normalizePldDescriptionSource } from "@/store/constants";

export const utilityMixin = {
  data: function () {
    return {
      colorsForPld: {
        5: ["pl5_red", "pl5_yellow", "pl5_green", "pl5_blue", "pl5_purple"],
        4: ["pl4_red", "pl4_yellow", "pl4_green", "pl4_blue"],
        3: ["pl3_red", "pl3_yellow", "pl3_green"],
        2: ["pl2_red", "pl2_green"],
        1: ["pl1"],
      },
    };
  },

  methods: {
    hasPercentCorrectMainScore(reportInfo) {
      if (reportInfo.visibleMeasures != null) {
        if (
          reportInfo.visibleMeasures.includes("Percent Correct") &&
          !reportInfo.visibleMeasures.includes("Scale Score")
        ) {
          return this.DisplayPercentCorrectIfAvailable(reportInfo);
        }
      }
    },
    hasScore(reportInfo) {
      if (!reportInfo) {
        return false;
      }
      if (this.hasPercentCorrectMainScore(reportInfo)) {
        return true;
      }
      return reportInfo.score && reportInfo.score.toLowerCase() !== "n/a";
    },
    getDecimalPrecision(displaySettings) {
      if (!displaySettings || !Array.isArray(displaySettings)) {
        return 0;
      }
      const decimalSetting = displaySettings.find(
        (setting) => typeof setting === "string" && setting.startsWith("Decimal:")
      );
      if (decimalSetting) {
        const parts = decimalSetting.split(":");
        if (parts.length === 2) {
          const precision = parseInt(parts[1], 10);
          return isNaN(precision) ? 0 : precision;
        }
      }
      return 0;
    },
    getSchoolyearString(selectedSchoolYear) {
      var translationKey = "StudentInfoBar.schoolYear";
      // Try with parameters first
      return this.$t(translationKey, { schoolyear: `${selectedSchoolYear}` });
    },
    safeT(key, fallback, params = {}) {
      const result = this.$t(key, params);
      return result === key ? fallback : result;
    },
    getAcceleratedMessageKey(clientname, testtype, testKey, selectedSchoolYear) {
      const clientName = clientname?.toLowerCase() ?? "";
      const normalizedTestKey = testKey?.toLowerCase() ?? "";
      const schoolYear = parseInt(selectedSchoolYear, 10);

      const isTexas = clientName === "texas";
      const isGrade3 = normalizedTestKey.includes("3");
      const is38Ela =
        testtype?.toLowerCase() === "staar38" && normalizedTestKey.includes("ela-reading");

      if (!isNaN(schoolYear) && schoolYear >= 2025 && isTexas && isGrade3 && is38Ela) {
        return this.$t("SubjectInfo.acceleratedmessage_G3RLA", {
          tutoringUrl: this.tutoringG3RLAUrl(),
        });
      }

      return this.$t("SubjectInfo.acceleratedmessage", {
        tutoringUrl: this.tutoringUrl(),
      });
    },
    /**
     * Returns the filtered pldinfo array based on reportInfo and useNominalIfPresent flag
     * @param {Object} reportInfo - The report info object containing pldinfo and other data
     * @param {boolean} useNominalIfPresent - Flag to determine filtering logic (defaults to false)
     * @returns {Array} Filtered pldinfo array
     */
    getPldInfo(reportInfo, useNominalIfPresent = false) {
      if (!reportInfo || !Array.isArray(reportInfo.pldinfo)) return [];

      let targetSetName = reportInfo.pldSetName;

      if (useNominalIfPresent && reportInfo.nominalPldSetName) {
        targetSetName = reportInfo.nominalPldSetName;
      }
      return reportInfo.pldinfo.filter((item) => item.pldSetName === targetSetName);
    },
    /**
     * Resolves whether getPldInfo should use nominal PLD data when building
     * the input for getPerformanceDescription.
     *
     * Rules:
     * - "Score Measure PLD" => false
     * - "Nominal Measure PLD" => true
     * - "Not Set" or null/undefined => keep the caller-provided fallback
     *
     * @param {Object} reportInfo - Student/report object from the API.
     * @param {boolean} useNominalIfPresent - Existing caller fallback behavior.
     * @returns {boolean} Boolean passed as the second argument to getPldInfo.
     */
    getPldInfoUseNominalForPerformanceDescription(reportInfo, useNominalIfPresent = false) {
      const pldDescriptionSource = normalizePldDescriptionSource(reportInfo?.pldDescriptionSource);

      if (pldDescriptionSource === PldDescriptionSources.ScoreMeasurePld) {
        return false;
      }

      if (pldDescriptionSource === PldDescriptionSources.NominalMeasurePld) {
        return true;
      }

      // if wave does not have PLD Description source set, fallback to hard-coded value in UI

      return useNominalIfPresent;
    },
    /**
     * Returns true/false based on if nominal set name is present
     * @param {Object} reportInfo - The report info object containing pldinfo and other data
     * @returns {Boolean} True if nominal set name is present, false otherwise
     */
    hasNominalPlds(reportInfo) {
      if (reportInfo.nominalPldSetName) {
        return true;
      }
      return false;
    },
    /**
     * check if performance level (can be used for icon, level_name, level text) should be displayed
     */
    checkPerformanceVisibility(visibleMeasures, performanceLevel) {
      if (
        (visibleMeasures == null ||
          (visibleMeasures != null && visibleMeasures.includes("Performance Level"))) &&
        performanceLevel &&
        performanceLevel.toLowerCase !== "n/a"
      ) {
        return true;
      }
      return false;
    },
    getPerformance(pldLevels, performance) {
      let level = "";
      if (pldLevels != null) {
        pldLevels.forEach((element) => {
          if (performance == element.code) {
            level = element.level;
            return;
          }
        });
      }

      return level;
    },
    getPerformanceDescription(pldLevels, performance) {
      let description = "";

      if (pldLevels != null) {
        if (pldLevels.length == 1 && pldLevels[0].level) {
          description = pldLevels[0].description;
        }
        pldLevels.forEach((element) => {
          if (performance == element.code) {
            description = element.description;
            return;
          }
        });
      }
      return description;
    },

    hasCutPoints(pldInfo) {
      return (
        pldInfo &&
        pldInfo.length > 0 &&
        pldInfo[0].loCut >= 0 &&
        pldInfo[0].hiCut > 0 &&
        pldInfo[0].loCut != pldInfo[0].hiCut
      );
    },

    DisplayOverrideForScore(score, test, clientname) {
      if (clientname.toLowerCase() == "texas") {
        let isValidTest =
          (test?.includes("Spanish") || test?.includes("En Español")) &&
          (test?.includes("Reading") || test?.includes("Lectura"));
        if (isValidTest) {
          return "S-" + score;
        }
      }
      return score;
    },
    DisplayPercentCorrectIfAvailable(reportInfo) {
      if (reportInfo.visibleMeasures == null || reportInfo.miscdata == null) {
        return false;
      }
      if (
        reportInfo.visibleMeasures.includes("Percent Correct") &&
        reportInfo.miscdata.percentcorrect
      ) {
        return true;
      }
    },
    DisplayDistrictPercentileIfAvailable(reportInfo) {
      if (reportInfo.visibleMeasures == null || reportInfo.miscdata == null) {
        return false;
      }
      if (
        reportInfo.visibleMeasures.includes("Additional Percentile") &&
        reportInfo.miscdata.districtpercentile
      ) {
        return true;
      }
    },
    DisplayPercentileIfAvailable(reportInfo) {
      if (reportInfo.additionalScores == null) {
        return false;
      }
      if (reportInfo.additionalScores["Percentile"]) {
        return true;
      }
    },

    DisplayLexileRangeIfAvailable(reportInfo) {
      if (reportInfo.visibleMeasures == null || reportInfo.miscdata == null) {
        return false;
      }
      if (
        reportInfo.visibleMeasures.includes("Lexile Range") &&
        reportInfo.miscdata.lexilerangell &&
        reportInfo.miscdata.lexilerangeul
      ) {
        return true;
      }
    },
  
    // load title and description based on test type and placeholderkey stem with option to load only title without description toggle
    toggleDescriptionData(
      testtypes,
      testtype,
      placeholderkeystem,
      titleonly = false,
      translationParams = {}
    ) {
      const testtypelower = testtype?.replace(/\./g, "").toLowerCase() || "";
      // Check if datatype matches a config type
      const match = testtypes?.find(
        (c) => testtypelower.includes(c.type) && testtypelower.includes(c.prefix)
      );
      if (match) {
        // If datatype is in the configs list
        const titleKey = `${placeholderkeystem}_${match.type}`;
        // Check if the translation key exists by attempting to translate it
        const title = this.$t(titleKey, translationParams);
        // If translation exists (i.e., $t doesn't return the key itself)
        if (title !== titleKey) {
          // Key exists but empty = intentionally suppressed for this test type
          if (title === "") return null;
          return {
            title: title + (titleonly ? ":" : ""),
            description: titleonly
              ? ""
              : this.safeT(`${placeholderkeystem}Description_${match.type}`, "", translationParams),
          };
        }
      }
      // If datatype is NOT in configs list, no toggle display but just the title if available
      // Fallback to client level translation
      const title = this.safeT(`${placeholderkeystem}`, "", translationParams);
      if (title !== "") {
        return {
          title: title + (titleonly ? ":" : ""),
          description: titleonly
            ? ""
            : this.safeT(`${placeholderkeystem}Description`, "", translationParams),
        };
      }
      return null;
    },
    getTestTypeObject(testtypes, testtype) {
      const testtypelower = testtype?.replace(/\./g, "").toLowerCase() || "";
      // Check if datatype matches a config type
      const match = testtypes?.find(
        (c) => testtypelower.includes(c.type) && testtypelower.includes(c.prefix)
      );
      return match;
    },

    DisplayUnifiedScaleScoreIfAvailable(reportInfo) {
      if (reportInfo.visibleMeasures == null || reportInfo.miscdata == null) {
        return false;
      }
      if (
        reportInfo.visibleMeasures.includes("Unified Scale Score") &&
        reportInfo.miscdata.unifiedscalescore
      ) {
        return true;
      }
    },
    DisplayScore(reportInfo) {
      if (reportInfo.displaySettings == null) {
        return reportInfo.score;
      } else if (reportInfo.displaySettings.includes("Fraction")) {
        const pldInfo = this.getPldInfo(reportInfo);
        if (pldInfo && pldInfo.length > 0) {
          const singlePld = pldInfo[pldInfo.length - 1];
          if (
            reportInfo.score.toLowerCase() !== "n/a" &&
            singlePld.hiCut != 0 &&
            singlePld.hiCut >= reportInfo.score
          ) {
            return `${reportInfo.score}/${singlePld.hiCut}`;
          }
        }
        return reportInfo.score;
      } else if (reportInfo.displaySettings.includes("Percentile")) {
        return reportInfo.score + "%";
      } else if (
        reportInfo.displaySettings.some(
          (setting) => typeof setting === "string" && setting.startsWith("Decimal:")
        )
      ) {
        return this.DisplayScoreWithPrecision(reportInfo.score, reportInfo);
      } else {
        return reportInfo.score;
      }
    },

    DisplayCategoryScore(reportInfo, categoryScoreObject) {
      if (reportInfo.displaySettings == null) {
        return this.DisplayScoreWithPrecision(categoryScoreObject.score);
      } else if (reportInfo.displaySettings.includes("Fraction")) {
        if (categoryScoreObject.pld && categoryScoreObject.pld.length > 0) {
          const singlePld = categoryScoreObject.pld[categoryScoreObject.pld.length - 1];
          if (
            categoryScoreObject.score.toLowerCase() !== "n/a" &&
            singlePld.hiCut != 0 &&
            singlePld.hiCut >= categoryScoreObject.score
          ) {
            return `${categoryScoreObject.score}/${singlePld.hiCut}`;
          }
        }
        return this.DisplayScoreWithPrecision(categoryScoreObject.score);
      } else {
        return this.DisplayScoreWithPrecision(categoryScoreObject.score);
      }
    },

    DisplayScoreWithPrecision(score, reportInfo = null) {
      // checking for numeric score
      let fractionDigits = 0; // default to 0 decimal places

      if (reportInfo && reportInfo.displaySettings) {
        fractionDigits = this.getDecimalPrecision(reportInfo.displaySettings);
      }

      if (!isNaN(parseFloat(score)) && isFinite(score)) {
        return parseFloat(score).toFixed(fractionDigits);
      }
      return score;
    },

    getViewDetailedReportUrl(opp, schoolYear) {
      let detailUrl = "/details";
      if (schoolYear) {
        detailUrl += `/${schoolYear}`;
      }
      if (opp.subject) {
        let sub = encodeURIComponent(opp.subject);
        detailUrl += `/${sub}`;
      }
      detailUrl += `/${opp.oppKey}`;
      return detailUrl;
    },

    BarrelChartPositionClass(scorelength) {
      if (typeof scorelength != "undefined" && scorelength == 0) {
        return "test_performance test_performance_align_center";
      } else {
        return "test_performance";
      }
    },
    // show No Raiting Available for telpas tests with 0 performance level or No Rating Available in performance level name
    // to avoid to show it twice in level name and level text, check to skip in level text display
    IsTelpasNoRatingTest(scoredetails) {
      // checking if it is a telpas 0 performance level / no score data available
      if (
        scoredetails.testType.startsWith("telpas") &&
        (scoredetails.performanceLevel == "No Rating Available" ||
          scoredetails.performanceLevel == 0)
      ) {
        return true;
      }
      return false;
    },
    formatLexileQuantileScore(score, isLexile) {
      var formattedScore = score;
      const firstCharacter = formattedScore.charAt(0);
      if (firstCharacter != null && firstCharacter !== "" && /-/.test(firstCharacter)) {
        formattedScore = (isLexile ? "BR" : "EM") + formattedScore.substring(1);
      }
      const lastCharacter = formattedScore.charAt(formattedScore.length - 1);
      if (lastCharacter != null && lastCharacter !== "" && /\d/.test(lastCharacter)) {
        formattedScore = formattedScore + (isLexile ? "L" : "Q");
      }
      return formattedScore;
    },
    getPercentileSuffix(score) {
      if (score.indexOf("1") != "-1" && score.indexOf("1") == score.length - 2) {
        return "th";
      } else if (score.lastIndexOf("1") == score.length - 1) {
        return "st";
      } else if (score.lastIndexOf("2") == score.length - 1) {
        return "nd";
      } else if (score.lastIndexOf("3") == score.length - 1) {
        return "rd";
      } else {
        return "th";
      }
    },

    getDirAttributeValue(oppInfo, lanKey) {
      let value = "ltr";
      if (lanKey === "ar") {
        return oppInfo.testType.toLowerCase().trim() === "ilearn checkpoints" ? "ltr" : "rtl";
      }
      return value;
    },
    getSubjectNameForCssAttrib(subject) {
      if (!subject) return "";

      // Replace all punctuation with underscores
      return subject.replace(/[^\w\s]/g, "_");
    },
    //generic filtering of array with multiple filter criteria
    // filteredArray(mainArray, filterCriteria) {
    //   const lowerCaseFilterCriteria = filterCriteria.map((criteria) => criteria.toLowerCase());

    //   return mainArray.filter((item) => {
    //     const lowerCaseItem = item.toLowerCase();
    //     return lowerCaseFilterCriteria.some((criteria) => lowerCaseItem.includes(criteria));
    //   });
    // },
  },
};
